// F-INTER: integração Banco Inter Empresas (OAuth2 + mTLS).
// Configura credenciais, certificado + chave privada (upload em S3),
// testa conexão e sincroniza extrato de forma manual ou via cron.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Alert, Tag, Typography, Space, Flex, Row, Col,
  DatePicker, Input, App as AntApp, Form, Radio,
} from 'antd';
import {
  SyncOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { getToken } from '../../utils/auth';

const { Text } = Typography;

export default function IntegracaoInter() {
  const { message, modal } = AntApp.useApp();
  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [form, setForm] = useState<any>({
    client_id: '', client_secret: '', conta_corrente: '',
    cert_s3_key: '', cert_key_s3_key: '', cert_validade: null,
    ambiente: 'producao', scopes: ['extrato.read'],
  });
  const [periodoSync, setPeriodoSync] = useState<[any, any] | null>(null);
  const [historico, setHistorico] = useState<any[]>([]);
  async function carregarHistorico() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/admin/inter/historico?limit=10`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setHistorico(Array.isArray(d.historico) ? d.historico : []);
    } catch { /* silencia — banner some se falhar */ }
  }
  useEffect(() => { carregarHistorico(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/admin/inter/config`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCfg(d);
      if (d) {
        setForm({
          client_id: d.client_id || '',
          client_secret: '',  // nunca pre-popular secret
          conta_corrente: d.conta_corrente || '',
          cert_s3_key: d.cert_s3_key || '',
          cert_key_s3_key: d.cert_key_s3_key || '',
          cert_validade: d.cert_validade || null,
          ambiente: d.ambiente || 'producao',
          scopes: d.scopes || ['extrato.read'],
        });
      }
    } catch (e: any) {
      console.warn('Erro ao carregar config Inter:', e.message);
    } finally { setLoading(false); }
  }

  useEffect(() => { carregar(); }, []);

  async function uploadArquivo(tipo: 'cert' | 'key', file: File) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(',')[1];
          const token = await getToken();
          const r = await axios.post(`${API_URL}/admin/inter/upload-cert`,
            { tipo, filename: file.name, content_base64: base64 },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          resolve(d.s3_key);
        } catch (e: any) {
          reject(new Error(e.response?.data?.error || e.message));
        }
      };
      reader.onerror = () => reject(new Error('falha lendo arquivo'));
      reader.readAsDataURL(file);
    });
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      // Trim em tudo (cola do navegador as vezes traz espaco/newline invisivel)
      const clientId = (form.client_id || '').trim();
      const clientSecret = (form.client_secret || '').trim();
      const contaCorrente = (form.conta_corrente || '').trim();
      // Validacao formato UUID: client_id e secret do Inter sao UUIDs (36
      // chars com 4 hifens). Se estiver fora desse formato, avisa antes
      // de mandar (sem bloquear — talvez seja um formato novo).
      const reUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      if (clientId && !reUuid.test(clientId)) {
        message.warning(`Client ID com ${clientId.length} chars — formato Inter padrão é UUID com 36 chars (8-4-4-4-12). Confira se copiou completo.`);
      }
      if (clientSecret && !reUuid.test(clientSecret)) {
        message.warning(`Client Secret com ${clientSecret.length} chars — formato Inter padrão é UUID com 36 chars.`);
      }
      await axios.put(`${API_URL}/admin/inter/config`, {
        client_id: clientId || null,
        client_secret: clientSecret || null,
        conta_corrente: contaCorrente || null,
        cert_s3_key: form.cert_s3_key || null,
        cert_key_s3_key: form.cert_key_s3_key || null,
        cert_validade: form.cert_validade || null,
        scopes: form.scopes,
        ambiente: form.ambiente,
        ativo: true,
      }, { headers: { Authorization: token } });
      message.success('Configuração salva.');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function testarSaldo() {
    setTestando(true);
    setResultado(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/admin/inter/test-saldo`, {},
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      if (d.ok) message.success('Conexão OK!');
      else message.error('Falha: ' + (d.erro || d.error));
    } catch (e: any) {
      setResultado({ ok: false, error: e.response?.data?.error || e.message });
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setTestando(false); }
  }

  async function sincronizar() {
    setSincronizando(true);
    setResultado(null);
    try {
      const token = await getToken();
      const body: any = {};
      if (periodoSync && periodoSync[0] && periodoSync[1]) {
        body.data_inicio = periodoSync[0].format('YYYY-MM-DD');
        body.data_fim = periodoSync[1].format('YYYY-MM-DD');
      }
      const r = await axios.post(`${API_URL}/financeiro/inter/sync-extrato`, body,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setResultado(d);
      message.success(`Sincronizado: ${d.inseridas} novas, ${d.duplicadas} duplicadas, ${d.erros} erros`);
      await carregarHistorico();
    } catch (e: any) {
      setResultado({ ok: false, error: e.response?.data?.error || e.message });
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSincronizando(false); }
  }

  if (loading) return <Card loading />;

  const configurado = !!(cfg?.client_id && cfg?.tem_secret && cfg?.cert_s3_key && cfg?.cert_key_s3_key);
  const certVenceEm = cfg?.cert_validade
    ? Math.round((new Date(cfg.cert_validade).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <div>
      {/* Status */}
      <Card size="small" style={{ marginBottom: 12,
        background: configurado ? '#F6FFED' : '#FFF7E6',
        border: `1px solid ${configurado ? '#B7EB8F' : '#FFD591'}` }}>
        <Space wrap>
          <Tag color={configurado ? 'success' : 'warning'} icon={configurado ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}>
            {configurado ? 'Configurado' : 'Não configurado'}
          </Tag>
          {cfg?.ambiente && <Tag>{cfg.ambiente}</Tag>}
          {certVenceEm !== null && (
            <Tag color={certVenceEm < 30 ? 'red' : certVenceEm < 90 ? 'orange' : 'blue'}>
              Cert vence em {certVenceEm} dias
            </Tag>
          )}
          {cfg?.atualizado_em && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              · atualizado em {fmtDataBr(cfg.atualizado_em)} por {cfg.atualizado_por}
            </Text>
          )}
        </Space>
      </Card>

      {/* Banner do ÚLTIMO SYNC (cron + manual) — visivel sem precisar
          abrir CloudWatch. Mostra qtd novas, dups, conciliadas, erros. */}
      {historico[0] && (() => {
        const h = historico[0];
        const erro = h.erros > 0 || h.erro_msg;
        const ok = !erro && h.concluido_em;
        return (
          <Card size="small" style={{
            marginBottom: 12,
            background: erro ? '#FDECEC' : ok ? '#F6FFED' : '#FFF7E6',
            borderColor: erro ? '#FFCCC7' : ok ? '#B7EB8F' : '#FFD591',
          }}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
              <div>
                <Text strong style={{ fontSize: 12, color: erro ? '#B91626' : ok ? '#0a7f4a' : '#B07500' }}>
                  {erro ? '✗' : ok ? '✓' : '⏳'} Último sync ({h.origem === 'cron' ? '🤖 cron 06h' : '👤 manual'}):{' '}
                  {dayjs(h.iniciado_em).format('DD/MM HH:mm')}
                  {h.duracao_ms && ` · ${(h.duracao_ms / 1000).toFixed(1)}s`}
                </Text>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                  Período {dayjs(h.data_inicio).format('DD/MM')}–{dayjs(h.data_fim).format('DD/MM')} ·{' '}
                  {h.total_no_extrato ?? '–'} no extrato ·{' '}
                  <strong style={{ color: '#0a7f4a' }}>{h.inseridas ?? 0} novas</strong> ·{' '}
                  {h.duplicadas ?? 0} duplicadas
                  {h.auto_conciliadas != null && h.auto_conciliadas > 0 && (
                    <> · 🔗 <strong style={{ color: '#0930D0' }}>{h.auto_conciliadas} auto-conciliadas</strong></>
                  )}
                  {erro && <> · <strong style={{ color: '#B91626' }}>{h.erros || '?'} erros</strong></>}
                </div>
              </div>
              <Button size="small" type="link" onClick={carregarHistorico}>
                🔄 Atualizar
              </Button>
            </Flex>
          </Card>
        );
      })()}

      {/* Histórico expandido (últimos 10) */}
      {historico.length > 1 && (
        <details style={{ marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: '#666', padding: '4px 0' }}>
            📜 Ver histórico (últimos {historico.length} syncs)
          </summary>
          <Card size="small" style={{ marginTop: 8 }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 4 }}>Quando</th>
                  <th style={{ padding: 4 }}>Origem</th>
                  <th style={{ padding: 4 }}>Período</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Extrato</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Novas</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Dup</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Conc</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Erros</th>
                  <th style={{ padding: 4, textAlign: 'right' }}>Tempo</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((h: any) => (
                  <tr key={h.id} style={{ borderBottom: '0.5px solid #f5f5f5' }}>
                    <td style={{ padding: 4 }}>{dayjs(h.iniciado_em).format('DD/MM HH:mm')}</td>
                    <td style={{ padding: 4 }}>{h.origem === 'cron' ? '🤖' : '👤'} {h.origem}</td>
                    <td style={{ padding: 4 }}>{dayjs(h.data_inicio).format('DD/MM')}–{dayjs(h.data_fim).format('DD/MM')}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{h.total_no_extrato ?? '–'}</td>
                    <td style={{ padding: 4, textAlign: 'right', color: h.inseridas > 0 ? '#0a7f4a' : '#888' }}>
                      {h.inseridas ?? 0}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right', color: '#888' }}>{h.duplicadas ?? 0}</td>
                    <td style={{ padding: 4, textAlign: 'right', color: h.auto_conciliadas > 0 ? '#0930D0' : '#888' }}>
                      {h.auto_conciliadas ?? 0}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right', color: h.erros > 0 ? '#B91626' : '#888' }}>
                      {h.erros ?? 0}
                    </td>
                    <td style={{ padding: 4, textAlign: 'right' }}>
                      {h.duracao_ms ? `${(h.duracao_ms / 1000).toFixed(1)}s` : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </details>
      )}

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Como conseguir as credenciais"
        description={
          <ol style={{ marginBottom: 0, paddingLeft: 18, fontSize: 12 }}>
            <li>Logue no Internet Banking PJ Inter</li>
            <li>Vá em <strong>Soluções para sua empresa → Nova integração</strong></li>
            <li>Marque escopo <code>extrato.read</code> (mínimo) — adicione cobrança se for emitir boletos depois</li>
            <li>Baixe os 3 artefatos: <strong>client_id, client_secret, certificado (.crt + .key)</strong></li>
            <li>Faça upload do .crt e .key abaixo + cole client_id/secret → Salvar → Testar conexão</li>
            <li>Cert vive 1 ano. Renovação = nova integração (gera novo id/secret também).</li>
          </ol>
        } />

      {/* Credenciais */}
      <Card size="small" title="🔐 Credenciais OAuth2 + mTLS" style={{ marginBottom: 12 }}>
        <Form layout="vertical" size="small">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Client ID" required
                extra={(() => {
                  const v = (form.client_id || '').trim();
                  if (!v) return 'UUID com 36 chars (formato 8-4-4-4-12)';
                  const re = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                  if (re.test(v)) return `✓ ${v.length} chars — formato UUID OK`;
                  return `⚠ ${v.length} chars — esperado 36 chars no formato UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)`;
                })()}>
                <Input value={form.client_id}
                  onChange={e => setForm({ ...form, client_id: e.target.value })}
                  onBlur={e => setForm({ ...form, client_id: e.target.value.trim() })}
                  placeholder="ex: 11e353b4-2480-4875-878d-5c58d6e7cd59"
                  autoComplete="off" spellCheck={false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Client Secret"
                extra={(() => {
                  const v = (form.client_secret || '').trim();
                  if (!v) return cfg?.tem_secret ? '✓ secret já cadastrado — deixe vazio pra preservar.' : 'UUID com 36 chars';
                  const re = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                  if (re.test(v)) return `✓ ${v.length} chars — formato UUID OK`;
                  return `⚠ ${v.length} chars — esperado 36 chars no formato UUID`;
                })()}>
                <Input.Password value={form.client_secret}
                  onChange={e => setForm({ ...form, client_secret: e.target.value })}
                  onBlur={e => setForm({ ...form, client_secret: e.target.value.trim() })}
                  placeholder={cfg?.tem_secret ? '••••• (preservar)' : 'cole o secret aqui'}
                  autoComplete="off" spellCheck={false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Conta-corrente"
                extra="Obrigatório se PJ tem múltiplas contas. Vira header x-conta-corrente.">
                <Input value={form.conta_corrente} onChange={e => setForm({ ...form, conta_corrente: e.target.value })}
                  placeholder="ex: 12345678" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Ambiente">
                <Radio.Group value={form.ambiente} onChange={e => setForm({ ...form, ambiente: e.target.value })}>
                  <Radio value="producao">Produção</Radio>
                  <Radio value="sandbox">Sandbox</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={<><SafetyCertificateOutlined /> Certificado .crt</>}
                extra={form.cert_s3_key ? `📎 ${form.cert_s3_key.split('/').pop()}` : 'arquivo .crt baixado do painel Inter'}>
                <input type="file" accept=".crt,.pem"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const k = await uploadArquivo('cert', f);
                      setForm((s: any) => ({ ...s, cert_s3_key: k }));
                      message.success('Cert enviado.');
                    } catch (err: any) {
                      message.error('Erro: ' + err.message);
                    }
                  }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={<><SafetyCertificateOutlined /> Chave privada .key</>}
                extra={form.cert_key_s3_key ? `📎 ${form.cert_key_s3_key.split('/').pop()}` : 'arquivo .key baixado do painel Inter (NÃO compartilhe!)'}>
                <input type="file" accept=".key,.pem"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const k = await uploadArquivo('key', f);
                      setForm((s: any) => ({ ...s, cert_key_s3_key: k }));
                      message.success('Chave enviada.');
                    } catch (err: any) {
                      message.error('Erro: ' + err.message);
                    }
                  }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Validade do certificado"
                extra="Pra alerta 30d antes do vencimento. Cert Inter dura 1 ano.">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                  value={form.cert_validade ? dayjs(form.cert_validade) : null}
                  onChange={d => setForm({ ...form, cert_validade: d ? d.format('YYYY-MM-DD') : null })} />
              </Form.Item>
            </Col>
          </Row>
          <Button type="primary" loading={salvando} onClick={salvar}>Salvar configuração</Button>
        </Form>
      </Card>

      {/* Teste */}
      <Card size="small" title="🩺 Validação" style={{ marginBottom: 12 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text style={{ fontSize: 12, color: '#555' }}>
            Faz uma chamada simples em <code>GET /banking/v2/saldo</code> pra validar que credenciais + cert estão corretos.
          </Text>
          <Button icon={<CheckCircleOutlined />} loading={testando} onClick={testarSaldo} disabled={!configurado}>
            Testar conexão
          </Button>
        </Space>
      </Card>

      {/* Sync */}
      <Card size="small" title="🔄 Sincronizar extrato manualmente">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text style={{ fontSize: 12, color: '#555' }}>
            Busca transações no Inter e insere em <code>financeiro.transacoes_bancarias</code> (dedup automático
            por banco+conta+data+valor).
            Default: últimos 7 dias se range não preenchido.
          </Text>
          <DatePicker.RangePicker size="small" format="DD/MM/YYYY"
            value={periodoSync as any}
            onChange={d => setPeriodoSync(d as any)} />
          <Space>
            <Button type="primary" icon={<SyncOutlined />} loading={sincronizando} onClick={sincronizar} disabled={!configurado}>
              Sincronizar agora
            </Button>
            <Button danger loading={sincronizando}
              onClick={async () => {
                modal.confirm({
                  title: 'Limpar duplicatas API ↔ OFX',
                  content: 'Apaga transações vindas do API Inter que duplicam OFX já importado (mesma conta+data+valor). Preserva o OFX (e suas classificações/vínculos). Não apaga conciliadas.',
                  okText: 'Limpar', cancelText: 'Cancelar',
                  onOk: async () => {
                    setSincronizando(true);
                    try {
                      const token = await getToken();
                      const r = await axios.post(`${API_URL}/admin/inter/limpar-duplicatas`, {},
                        { headers: { Authorization: token } });
                      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
                      setResultado(d);
                      message.success(`${d.deletadas} duplicatas removidas (${d.candidatas} candidatas).`);
                    } catch (e: any) {
                      message.error('Erro: ' + (e.response?.data?.error || e.message));
                    } finally { setSincronizando(false); }
                  },
                });
              }}>
              🧹 Limpar duplicatas API ↔ OFX
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Resultado da última operação */}
      {resultado && (
        <Card size="small" title="📊 Resultado" style={{ marginTop: 12 }}>
          <pre style={{ fontSize: 11, maxHeight: 300, overflow: 'auto', margin: 0 }}>
            {JSON.stringify(resultado, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
}
